import { useMemo } from 'react';
import { useSubscription } from '../../lib/subscription/useSubscription';
import { useAccount } from '../../lib/account/useAccount';
import { useReactivateSubscriptionMutation } from '../../lib/subscription/useReactivateSubscriptionMutation';
import { subscriptionCanRenew } from '../../lib/subscription/subscription.helpers';
import { track } from '../../lib/ui-usage';
import { formatLongDateTime } from '@packages/format';
import { Button, ButtonLink } from '../ui/Button';

function daysUntil(dateIso: string): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  const diff = new Date(dateIso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / msPerDay));
}

const TRIAL_BANNER_THRESHOLD_DAYS = 7;

export function SubscriptionBanner() {
  const { subscription, needsSubscription } = useSubscription();
  const { isOwnerOrAssistant, administeredBy } = useAccount();
  const reactivateMutation = useReactivateSubscriptionMutation();

  const isActiveTrial = useMemo(
    () =>
      !!subscription &&
      subscription.status === 'TRIALING' &&
      !!subscription.trialEnd &&
      new Date(subscription.trialEnd) > new Date(),
    [subscription],
  );

  // Only nudge the user once the trial is within its final week. Showing a
  // countdown banner on day 1 of a 14-day trial just adds noise.
  const trialRemaining = isActiveTrial && subscription ? daysUntil(subscription.trialEnd!) : Infinity;
  const showTrialBanner = isActiveTrial && trialRemaining <= TRIAL_BANNER_THRESHOLD_DAYS;

  // Deferred cancellation: access is retained (needsSubscription stays false)
  // until currentPeriodEnd, so this branch needs its own guard alongside the
  // trial one rather than falling out of the needsSubscription check below.
  const isCancelling = subscription?.status === 'CANCELLING';

  if (!needsSubscription && !showTrialBanner && !isCancelling) return null;

  // Inside an administered entity's books the subscription in hand is the
  // administering firm's (the read walks up), so every message here would
  // describe the firm's plan as if it were the entity's, and every Subscribe
  // link would land on a settings tab that refuses subscription writes for an
  // entity. The firm's own pages carry the nudge; the entity shows nothing.
  if (administeredBy) return null;

  if (isCancelling && subscription) {
    const periodEndDate = subscription.currentPeriodEnd ? formatLongDateTime(subscription.currentPeriodEnd) : null;
    const canRenew = subscriptionCanRenew(subscription);
    return (
      <div className="border-b border-primary/15 bg-bg-warm px-4 py-3 sm:px-6">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-start gap-2.5">
            <svg
              viewBox="0 0 24 24"
              className="mt-0.5 h-4 w-4 flex-shrink-0 stroke-primary"
              fill="none"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15 14" />
            </svg>
            <p className="text-sm text-secondary">
              {periodEndDate
                ? `Your subscription will be cancelled on ${periodEndDate}.`
                : 'Your subscription is set to cancel at the end of the current billing period.'}
              {' '}
              {canRenew
                ? 'Renew to keep your access.'
                : /* A provider-retired card (Paystack `subscription.not_renew`) writes CANCELLING with
                     no free renewal window, but access continues to currentPeriodEnd exactly as it does
                     for a self-service cancel - so the date sentence above still applies here. Only the
                     CTA differs: there is nothing left to renew for free, so the next step is a fresh
                     subscription rather than a reactivation. */
                  'This subscription cannot be renewed. Subscribe again to keep access after it ends.'}
            </p>
          </div>
          {canRenew ? (
            isOwnerOrAssistant && (
              <div className="flex flex-shrink-0 flex-col items-start gap-1">
                <Button
                  size="sm"
                  onClick={() => {
                    track('ui-usage.cta.clicked', { cta: 'subscription-banner-renew' });
                    reactivateMutation.mutate();
                  }}
                  loading={reactivateMutation.isPending}
                >
                  Renew
                </Button>
                {reactivateMutation.isError && (
                  <p className="text-xs text-error" role="alert">
                    {reactivateMutation.error instanceof Error
                      ? reactivateMutation.error.message
                      : "Couldn't renew subscription. Try again."}
                  </p>
                )}
              </div>
            )
          ) : (
            <ButtonLink
              to="/settings?tab=subscription"
              onClick={() => track('ui-usage.cta.clicked', { cta: 'subscription-banner-subscribe-again' })}
              size="sm"
              className="flex-shrink-0"
            >
              Subscribe again
            </ButtonLink>
          )}
        </div>
      </div>
    );
  }

  // Active trial within the final week: friendly countdown + subscribe CTA.
  if (showTrialBanner && !needsSubscription) {
    const remaining = trialRemaining;
    const dayLabel = remaining === 1 ? 'day' : 'days';
    return (
      <div className="border-b border-primary/15 bg-bg-warm px-4 py-3 sm:px-6">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-start gap-2.5">
            <svg
              viewBox="0 0 24 24"
              className="mt-0.5 h-4 w-4 flex-shrink-0 stroke-primary"
              fill="none"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15 14" />
            </svg>
            <p className="text-sm text-secondary">
              {remaining === 0
                ? 'Your free trial ends today.'
                : `${remaining} ${dayLabel} left on your free trial.`}
              {' '}Subscribe to keep access when it ends.
            </p>
          </div>
          <ButtonLink
            to="/settings?tab=subscription"
            onClick={() => track('ui-usage.cta.clicked', { cta: 'subscription-banner-trial-subscribe' })}
            size="sm"
            className="flex-shrink-0"
          >
            Subscribe
          </ButtonLink>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg-warm border-b border-accent/20 px-4 py-3 sm:px-6">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-start gap-2.5">
          <svg
            viewBox="0 0 24 24"
            className="mt-0.5 h-4 w-4 flex-shrink-0 stroke-accent"
            fill="none"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <p className="text-sm text-secondary">
            Your subscription is inactive. You can view your data, but creating or editing requires an active subscription.
          </p>
        </div>
        <ButtonLink
          to="/settings?tab=subscription"
          onClick={() => track('ui-usage.cta.clicked', { cta: 'subscription-banner-inactive-subscribe' })}
          size="sm"
          className="flex-shrink-0"
        >
          Subscribe
        </ButtonLink>
      </div>
    </div>
  );
}
